import { expandNodesToPath, cornersOf, movementsForCanvas, movementsTouching } from "src/utils/maps"
import { relationColor, relationOf } from "src/utils/teamColors"
import { seatIconFor } from "src/utils/cosmetics"
import { crownCells } from "src/utils/crowns"
import { foundingOrderedAt } from "src/utils/pendingOrders"
import { activeWorld } from "src/utils/world"

/* What the map draws, for one snapshot of one game, seen by one seat.

   This was inside the Game view, which was fine while the Game view was the only thing drawing a
   map of a real game. The replay then grew its own smaller version — owner sprites, fire, crowns,
   marching columns — and the two immediately disagreed: no cosmetics, no ally rings, no camps, and a different rule for what counts as a battle. A replay that draws a different map
   from the game is not a replay (owner, 2026-09-04).

   Pure and parameterised instead, so both views call the same one and there is nothing to keep in
   step. `viewerId` is *whose eyes* — it decides which cells get an ally ring rather than none, and
   which marching columns read as yours — and everything a live game has and a replay does not
   (a route being planned, the selected cell) is optional.

   Returns `{ elements, movements }` shaped exactly as `Canvas` documents them.

   `motion` is what the round's playback is still drawing itself (requirement 36, M4) — the
   `hiddenLive` of `utils/roundMotion.stateAt`: the stacks the timeline animates (their live icon is
   not drawn), the open-ground cells a landing has not reached yet (their standing sprite waits),
   and the sites of this round's new battles (their fire waits for the clash). Null at every other
   time, which is the board as it is. */


const asSet = (value) => (value instanceof Set ? value : new Set(Array.isArray(value) ? value : []))


/* The way a stack standing on a march faces, when the march itself says: its road ahead (the
   next cell, the short way round the seam), else — held in a fight — the way it came in. */
export const headingOfMarch = (movement) => {
  const columns = activeWorld().columns
  const step = (from, to) => {
    let dx = to[0] - from[0]
    if (Math.abs(dx) > columns / 2) dx -= Math.sign(dx) * columns
    const dy = to[1] - from[1]
    return dx === 0 && dy === 0 ? null : Math.atan2(dy, dx)
  }
  const path = movement.path || []
  const index = Math.max(0, Math.min(movement.current_index ?? 0, path.length - 1))
  if (path.length > index + 1) {
    const ahead = step(path[index], path[index + 1])
    if (ahead != null) return { angle: ahead, onRoad: true }
  }
  if (Array.isArray(movement.came_from) && path[index]) {
    const came = step(movement.came_from, path[index])
    if (came != null) return { angle: came }
  }
  return {}
}

/* Two `units` dicts of one player as one, in the board's `{unit: {number}}` shape. A headcount
   (`{count: n}`, from a server older than 36/V1) beside anything makes the whole a headcount: a
   composition with an unknown part is not a composition. */
const bodiesIn = (value) => (typeof value === "number" ? value : Number(value?.number ?? value?.count ?? 0) || 0)
export const mergeUnits = (a, b) => {
  const entries = [...Object.entries(a || {}), ...Object.entries(b || {})]
  if (entries.some(([unit]) => unit === "count")) {
    return { count: entries.reduce((sum, [, value]) => sum + bodiesIn(value), 0) }
  }
  const merged = {}
  entries.forEach(([unit, value]) => {
    merged[unit] = { number: (merged[unit]?.number ?? 0) + bodiesIn(value) }
  })
  return merged
}

/* Cells where both teams stand with their units (36/B6, owner 2026-10-26): the garrisons on the
   cell and the marches standing on it, by side — `{"<col>-<row>": {human, zombie}}`. The engine
   fights them when the round turns, so the map draws them fighting now: the board after a show,
   and the show from the last arrival there (`roundMotion.buildTimeline`'s `standoffs`). */
export const standoffCells = (metadata, teamOf) => {
  const sides = {}
  const add = (key, team, units) => {
    if (team !== "human" && team !== "zombie") return
    const cell = (sides[key] = sides[key] || {})
    cell[team] = mergeUnits(cell[team] || {}, units || {})
  }
  Object.entries(metadata?.cells || {}).forEach(([key, cell]) => {
    Object.entries(cell?.metadata_by_user || {}).forEach(([id, entry]) => {
      if (Object.values(entry?.units || {}).some((unit) => (unit?.number ?? 0) > 0)) add(key, teamOf(id), entry.units)
    })
  })
  ;(metadata?.movements || []).forEach((movement) => {
    const path = movement.path || []
    if (!path.length || movement.from_out_of_sight) return
    const index = Math.max(0, Math.min(movement.current_index ?? 0, path.length - 1))
    add(`${path[index][0]}-${path[index][1]}`, teamOf(movement.user_id), movement.units)
  })
  return Object.fromEntries(Object.entries(sides).filter(([, cell]) => cell.human && cell.zombie))
}

export const buildBoard = ({
  game,
  viewerId,
  ownEquipped = null,
  highlightedNodes = [],
  selectedCity = null,
  creatingMovement = false,
  trail = [],
  motion = null,
  facings = {},
}) => {
  const userId = viewerId
  const elements = {}
  const getPlayer = (pId) => game.players.find((p) => p.user.id === pId)
  const hidden = {
    stacks: asSet(motion?.movementIds),
    landed: asSet(motion?.landedCells),
    clashed: asSet(motion?.clashedSites),
    // Cells whose garrison the timeline is drawing as it stood before the fight (owner,
    // 2026-10-23): the board's own sprite there is the one after it.
    garrisons: asSet(motion?.garrisonCells),
    // Cells whose deed leaves later in the show (owner, 2026-10-23: "the cities disappear before
    // the stack gets there"): "col-row" → the owner it leaves, drawn as their city until then.
    lost: motion?.lostCells instanceof Map ? Object.fromEntries(motion.lostCells) : motion?.lostCells || {},
    // What is still to land on each of `landed`'s cells, per player (`roundMotion.stateAt`).
    landing: motion?.landingUnits || null,
  }
  /* What a player has standing on a cell while a landing of theirs is still on its way there: the
     units drawn less what the landing brings, so a camp that stood there before the round stays
     (game 250, round 17). Null when nothing is left — and for a landing whose units the show did
     not say, which keeps the whole cell for the landing as before. */
  const lessWhatLands = (coordinates, ownerId, units) => {
    if (!hidden.landed.has(coordinates)) return units
    if (!hidden.landing?.[coordinates]) return null
    const coming = hidden.landing[coordinates][String(ownerId)]
    if (!coming) return units
    const due = Object.values(coming).reduce((sum, n) => sum + n, 0)
    if (units && Object.prototype.hasOwnProperty.call(units, "count")) {
      const left = bodiesIn(units.count) - due
      return left > 0 ? { count: left } : null
    }
    const left = {}
    Object.entries(units || {}).forEach(([unit, value]) => {
      const n = bodiesIn(value) - (coming[unit] || 0)
      if (n > 0) left[unit] = { number: n }
    })
    return Object.keys(left).length ? left : null
  }
  /* A stack standing on ground nobody owns is drawn as what it is (requirement 36, S3): the
     formation of its composition (`utils/formation`) — no tent, no owner icon.
     The viewer's own stack is the one shown when several stand there, else the first; a guest
     inside somebody's *city* keeps the tent, and the city its owner's icon.

     Two groups of ONE player on the cell are one formation (owner, 2026-10-26: a march ordered
     this round from a camp drew only the tank left behind). Until the round turns the march has
     not left, so what stands there is the camp and the march together; the playback shows it go. */
  /* Which way the group faces (owner, 2026-10-26: the way its last movement left it). A march
     about to go faces its road (`onRoad`) and wins a merge; otherwise the heading the last show
     ended on, kept per player and cell (`facings`, from `roundMotion.finalFacings`). None: east. */
  const stackOwner = new WeakMap()
  const placeStack = (coordinates, { units, owner, angle = null, onRoad = false }) => {
    const relation = relationOf({ ownerId: owner.user.id, userId, teamOf })
    const existing = (elements[coordinates] || []).find((el) => el.type === "stack")
    const remembered = facings[`${owner.user.id}@${coordinates}`]
    const heading = angle ?? (Number.isFinite(remembered) ? remembered : null)
    if (existing && stackOwner.get(existing) === owner.user.id) {
      const merged = { ...existing, units: mergeUnits(existing.units, units) }
      if (onRoad || merged.angle == null) {
        if (heading == null) delete merged.angle
        else merged.angle = heading
      }
      stackOwner.set(merged, owner.user.id)
      elements[coordinates] = elements[coordinates].map((el) => (el === existing ? merged : el))
      return
    }
    if (existing && (existing.relation === "self" || relation !== "self")) return
    const element = { type: "stack", units: units || null, relation, team: owner.team }
    if (heading != null) element.angle = heading
    stackOwner.set(element, owner.user.id)
    elements[coordinates] = existing
      ? elements[coordinates].map((el) => (el === existing ? element : el))
      : [...(elements[coordinates] || []), element]
  }
  /* The seat's agreed colour first (owner's ask, 2026-09-04), then the account's own look for
     games that predate it. `ownEquipped` is the viewer's freshest wallet copy and only matters in
     that older case: a look equipped mid-game would otherwise not show until the next broadcast. */
  const skinOf = (seat) => seatIconFor(seat, seat.user.id === userId ? ownEquipped : null)
  const teamOf = (id) => game.metadata?.users?.[String(id)]?.team
  /* Who has a colony ordered where (owner, 2026-10-04: "the icon of the city disappears for
     humans when I'm colonizing"). The founders' workers are held by the order, so the free numbers
     the board is drawn from can read zero on that cell; the party is still standing there, and the
     orderer is counted as present for the tent and the sprite. */
  const foundersAt = Object.fromEntries(
    (game.metadata?.foundings || []).map(({ coordinates, user_id: founder }) => [
      `${coordinates?.[0]}-${coordinates?.[1]}`,
      String(founder),
    ])
  )
  // The same for a discharge (owner, 2026-10-23): the bodies retiring are held by the order and off
  // the free garrison, but they stand on the cell until the round turns.
  const retiringAt = {}
  ;(game.metadata?.discharges || []).forEach(({ coordinates, user_id: orderer }) => {
    const key = `${coordinates?.[0]}-${coordinates?.[1]}`
    retiringAt[key] = [...(retiringAt[key] || []), String(orderer)]
  })
  // The board the show plays over is the one after the round: a city that changed hands is drawn
  // as its old owner's until the show reaches that moment, and nothing of its new state meanwhile.
  const lostOwnerAt = (coordinates) => {
    const formerId = hidden.lost[coordinates]
    return formerId == null ? null : getPlayer(Number(formerId)) || null
  }
  Object.entries(game.metadata.cells).forEach(([coordinates, obj]) => {
    if (elements[coordinates] === undefined) elements[coordinates] = []
    const former = lostOwnerAt(coordinates)
    if (former) {
      elements[coordinates].push({ type: former.team, skin: skinOf(former) })
      return
    }
    // A cell can be neutral: troops arriving on unclaimed land create it with owner null, and
    // conquest can clear it. getPlayer then returns undefined, and reading .team threw, taking
    // the whole game view down. Requirement 05 called this out.
    const cellOwner = getPlayer(obj.owner)
    // The sprite is the *owner's* choice, not the viewer's — a cosmetic only its owner could
    // see would not be worth buying. It rides on the player payload, so an ally's and an
    // enemy's cells both draw as whatever they are wearing.
    elements[coordinates].push({
      type: cellOwner ? cellOwner.team : "neutral",
      skin: cellOwner ? skinOf(cellOwner) : undefined,
    })
    /* Somebody standing on a cell they do not own — a staged force, a fresh arrival, an
       expedition resting, and (owner's ask, 2026-09-04) an *ally garrisoned in a city*. The tent
       used to be drawn only on unowned ground, so the one question it could not answer was the
       one a 2v2 player actually asks: which of my cities is my partner holding for me, and which
       of theirs is being held by theirs.

       So it marks a guest anywhere: units on a cell whose owner is somebody else. And it is
       coloured by *relation*, not by team — mine green, an ally's blue, an enemy's red — because
       "whose troops are those" is the question, and the answer has to read the same whichever
       side the viewer is on. Same three classes the city modal and the minimap use. */
    const guests = Object.keys(obj.metadata_by_user || {})
      .filter(
        (id) =>
          Object.values(obj.metadata_by_user[id]?.units || {}).some((unit) => (unit?.number ?? 0) > 0) ||
          foundersAt[coordinates] === String(id) ||
          (retiringAt[coordinates] || []).includes(String(id))
      )
      .map((id) => getPlayer(Number(id)))
      .filter((seat) => seat && seat.user.id !== obj.owner)
    if (guests.length > 0) {
      // Your own force outranks a guest's on a cell you are sharing: it is yours the mark is
      // telling you about first.
      const shown = guests.find((seat) => seat.user.id === userId) || guests[0]
      if (!cellOwner) {
        // Nobody holds the cell: the campers are what the map draws there, as the sprite of what
        // they are (S3) — unless a landing is still on its way to this cell on the playback.
        const units = !hidden.garrisons.has(coordinates)
          ? lessWhatLands(coordinates, shown.user.id, obj.metadata_by_user?.[String(shown.user.id)]?.units)
          : null
        if (units) placeStack(coordinates, { units, owner: shown })
      } else {
        const relation = relationOf({ ownerId: shown.user.id, userId, teamOf })
        elements[coordinates].push({
          type: "camp",
          team: shown.team,
          color: relationColor(relation),
        })
      }
    }
    // Both teams standing on the cell: a battle resolves here when the round turns. The map says
    // so with fire behind the sprite rather than leaving it to the numbers.
    const teamsPresent = new Set(
      Object.entries(obj.metadata_by_user || {})
        .filter(([, cellMetadata]) => Object.values(cellMetadata.units || {}).some((unit) => unit.number > 0))
        .map(([presentId]) => getPlayer(Number(presentId))?.team)
        .filter(Boolean)
    )
    if (teamsPresent.size > 1 && !hidden.clashed.has(coordinates)) elements[coordinates].push({ type: "combat" })
  })
  Object.keys(hidden.lost).forEach((coordinates) => {
    if (elements[coordinates]?.some((el) => el.type === "human" || el.type === "zombie")) return
    const former = lostOwnerAt(coordinates)
    if (former) elements[coordinates] = [...(elements[coordinates] || []), { type: former.team, skin: skinOf(former) }]
  })
  // The crowns: the up-to-three cities per player that decide their elimination. Marked for
  // everybody — ownership is public, and knowing which cities matter is the point — with the
  // badge faded once its player has lost it — for good, since 2026-10-04 (`crowns_lost`).
  Object.entries(crownCells(game.metadata, game.players)).forEach(([coordinates, crown]) => {
    elements[coordinates] = [...(elements[coordinates] || []), { type: "crown", held: crown.held }]
  })
  // The engine's own battle clock: a battle whose attacker stands at the gates as a movement
  // (2026-08-27) has one team living outside `metadata_by_user`, so the presence check above
  // misses it. `metadata.battles` is authoritative while a fight burns.
  Object.keys(game.metadata.battles || {}).forEach((key) => {
    if (hidden.clashed.has(key)) return
    if (!(elements[key] || []).some((el) => el.type === "combat")) {
      elements[key] = [...(elements[key] || []), { type: "combat" }]
    }
  })
  /* No rings on the game map (owner, 2026-10-26, `requirements/36` S7: "Remove all rings. I don't
     think any is needed. We have the crowns and the icons which tell everything we need to know"):
     the ally's city ring (2026-08-21), the enemy sighting ring (2026-09-07) and the survival wave's
     ring (SV12) are gone. A city's icon says whose it is, a crown says it counts, and since S6 every
     figure stands on its relation's colour. The lobby still rings the player's own starting cells.

     The route being drawn is one outline round every cell it goes through, in the selected cell's yellow, and no fill (owner,
     2026-10-26: "We need an outer red border for all cells the movement goes through"): each cell
     of the walked path carries the sides whose neighbour is not on the route, the short way round
     the seam. A single picked cell (the route's origin, before a target) is framed whole. */
  const columns = activeWorld().columns
  const routeCells = (highlightedNodes.length > 1
    ? expandNodesToPath(highlightedNodes).map(({ column, row }) => [column, row])
    : highlightedNodes.map((node) => [node.col, node.row])
  ).map(([column, row]) => [((column % columns) + columns) % columns, row])
  const onRoute = new Set(routeCells.map(([column, row]) => `${column}-${row}`))
  const wrapped = (column) => ((column % columns) + columns) % columns
  routeCells.forEach(([column, row]) => {
    const coordinates = `${column}-${row}`
    if (elements[coordinates] === undefined) elements[coordinates] = []
    if (elements[coordinates].some((el) => el.type === "highlight")) return
    elements[coordinates].push({
      type: "highlight",
      edges: {
        top: !onRoute.has(`${column}-${row - 1}`),
        right: !onRoute.has(`${wrapped(column + 1)}-${row}`),
        bottom: !onRoute.has(`${column}-${row + 1}`),
        left: !onRoute.has(`${wrapped(column - 1)}-${row}`),
      },
    })
  })
  // The selected cell keeps its selector whatever mode the map is in (owner's R17): the yellow
  // frame used to appear only while a route was being drawn, so a city under the action bar
  // had nothing on the map saying which one it was.
  if (selectedCity && !creatingMovement) {
    const coordinates = `${selectedCity.col}-${selectedCity.row}`
    elements[coordinates] = [...(elements[coordinates] || []), { type: "selected" }]
  }
  // A missile in the air (owner, 2026-10-02): a crosshair on the square it lands on when the round
  // turns. Only the firer's team is sent one (`games/visibility`, `fog.js`).
  ;(game.metadata?.strikes || []).forEach(({ target }) => {
    if (!target) return
    const key = `${target[0]}-${target[1]}`
    elements[key] = [...(elements[key] || []), { type: "strike" }]
  })
  // A tunnel entry or exit ordered this round (owner, 2026-10-05: "a separate icon like the misile
  // attack"): a mouth with an arrow, down for in and up for out, on the tunnel city it is made at
  // when the round turns. Only the orderer's team is sent one.
  ;(game.metadata?.tunnel_orders || []).forEach(({ coordinates, direction }) => {
    if (!coordinates) return
    const key = `${coordinates[0]}-${coordinates[1]}`
    elements[key] = [...(elements[key] || []), { type: "tunnel", direction }]
  })
  // A colony ordered this round (owner, 2026-10-02): founded when the round turns, so it is drawn
  // as a swinging pick in the cell's corner (laid over the canvas by `Canvas`), never as a city.
  // Only the orderer's team is sent one.
  ;(game.metadata?.foundings || []).forEach(({ coordinates }) => {
    if (!coordinates) return
    const key = `${coordinates[0]}-${coordinates[1]}`
    elements[key] = [...(elements[key] || []), { type: "founding" }]
  })
  // A discharge ordered this round (owner, 2026-10-23, requirement 36 D5): the same pick in the
  // same corner, since it is the same thing — an order that lands when the round turns. The
  // viewer's own carries `mine`, so a press on it can take the order back; an ally's is only drawn.
  ;(game.metadata?.discharges || []).forEach(({ coordinates, user_id: orderer }) => {
    if (!coordinates) return
    const key = `${coordinates[0]}-${coordinates[1]}`
    elements[key] = [...(elements[key] || []), { type: "discharge", mine: String(orderer) === String(userId) }]
  })
  // Every stack on the move, drawn from where it is to where it is going; then the route being
  // planned on top.
  // A march of that round still on the road (more than one round long) is in both lists, and drew
  // two lines to one destination (owner, 2026-10-05: "for a movement on >1 round, we see double
  // lines"). While the trail shows, the round as it was before it turned is the picture, so the
  // live road of a march the trail holds waits until the caption goes.
  const inTrail = new Set((trail || []).map((movement) => String(movement.id)))
  const live = (game.metadata?.movements || []).filter((movement) => !inTrail.has(String(movement.id)))
  const movements = [
    ...movementsForCanvas(live, { userId, teamOf }),
    // The round that just turned (owner, 2026-10-05), while "Round N" is announced: every road of
    // that round, ours included, from where each stack set out. Arrows only — the stacks themselves
    // stand where they arrived, on the live board.
    ...movementsForCanvas(trail, { userId, teamOf }).map((movement) => ({
      ...movement,
      id: `trail-${movement.id}`,
      trail: true,
    })),
  ]
  // The marching army itself (owner's Q24): its owner's icon at its current cell, exactly as
  // fog-off always drew it via metadata_by_user. Skipped where somebody already stands — an
  // ally's icon is already there, and an enemy's cell under a hostile stack gets the battle
  // fire instead of a second sprite.
  const standing = {}
  const standoffs = standoffCells(game.metadata, teamOf)
  ;(game.metadata?.movements || []).forEach((movement) => {
    const path = movement.path || []
    if (!path.length) return
    // A stack the playback is drawing itself is not drawn standing where it ended up (M4).
    if (hidden.stacks.has(String(movement.id))) return
    // A past road out of the fog (owner, 2026-10-05): the stack stood in the fog, not on the
    // first square the viewer saw, so there is no icon to draw — the line fades in instead.
    if (movement.from_out_of_sight) return
    const index = Math.max(0, Math.min(movement.current_index ?? 0, path.length - 1))
    const key = `${path[index][0]}-${path[index][1]}`
    const owner = getPlayer(movement.user_id)
    if (!owner) return
    ;(standing[key] = standing[key] || []).push({ movement, owner })
  })
  Object.entries(standing).forEach(([key, stacks]) => {
    const existing = elements[key] || []
    const occupant = existing.find((el) => el.type === "human" || el.type === "zombie")
    const teams = new Set(stacks.map(({ owner }) => owner.team))
    if (occupant) teams.add(occupant.type)
    // A garrison of the other side on the cell counts too: the winner of a fight earlier in the round
    // stands there as a camp when a late enemy halts beside it (36/B6).
    const fighting = teams.size > 1 || Boolean(standoffs[key]) || existing.some((el) => el.type === "combat")
    /* Who was here before the fight started. A garrison is obvious — the loop above has already
       drawn its sprite. For a stack it is `came_from`: the road behind a march is cut every round
       and only a stack that *walked* keeps the cell it stepped from (2026-09-07), so an arrival
       always has one and a stack caught where it stood — trying to leave, and intercepted on its
       own square — has none. */
    const wasAlreadyHere =
      Boolean(occupant) || stacks.some(({ movement }) => !Array.isArray(movement.came_from))
    let next = existing
    if (fighting && !hidden.clashed.has(key) && !next.some((el) => el.type === "combat")) {
      next = [...next, { type: "combat" }]
    }
    elements[key] = next
    /* Two columns that ran into each other on open ground draw fire and nothing else (owner,
       2026-09-09): there is no city and no camp there, and an icon would say somebody holds the
       square when the truth is that two marches collided on it. Whoever was already standing
       there still shows — it is theirs until they lose it.

       A stack standing on ground nobody holds is drawn as what it is (S3), the way a camp on
       `metadata_by_user` is above: the sprite of its composition over the relation ring. It used
       to be the owner's icon and a tent, read off `metadata_by_user` alone, so an army that had
       stopped on free ground — the ordinary shape of an expedition on its way — showed nothing
       until the round it deposited (owner, 2026-09-09). The same `wasAlreadyHere` decides the fire
       and the sprite, so the sheet cannot say one thing and the map another. On a city somebody
       holds, the city's icon is what stands there. */
    const unclaimed = (game.metadata.cells?.[key]?.owner ?? null) == null
    if (unclaimed && !occupant && (!fighting || wasAlreadyHere) && !hidden.garrisons.has(key) && hidden.lost[key] == null) {
      const shown =
        stacks.find(({ owner }) => owner.user.id === userId) ||
        stacks.find(({ movement }) => !Array.isArray(movement.came_from)) ||
        stacks[0]
      // Every group of that player here, so two orders out of one camp are drawn with it.
      const theirs = stacks.filter(({ owner }) => owner.user.id === shown.owner.user.id)
      if (!hidden.landed.has(key)) {
        theirs.forEach(({ movement, owner }) => placeStack(key, { units: movement.units, owner, ...headingOfMarch(movement) }))
      } else {
        // A landing still on its way: only what stood here before it (game 250, round 17).
        const all = theirs.reduce((acc, { movement }) => mergeUnits(acc, movement.units), null)
        const units = lessWhatLands(key, shown.owner.user.id, all)
        if (units) placeStack(key, { units, owner: shown.owner, ...headingOfMarch(theirs[0].movement) })
      }
    }
  })
  if (highlightedNodes.length > 1) {
    // Draw exactly the path the order will send: expandNodesToPath walks origin → target cell by
    // cell (an L for a diagonal leg, as when the target was picked before the origin), and the
    // canvas wants its corners.
    const cells = expandNodesToPath(highlightedNodes).map(({ column, row }) => [column, row])
    const corners = cornersOf(cells)
    if (corners.length > 1) {
      movements.push({
        type: "self",
        action: "attack",
        nodes: corners.map(([column, row]) => `${column}-${row}`),
        currentPosition: `${corners[0][0]}-${corners[0][1]}`,
      })
    }
  }
  /* A battle is drawn as the two armies fighting it (owner, 2026-10-26, `requirements/36` B1–B4):
     every battle cell carries each side's units — the garrisons on the cell and the stacks standing
     on it, allies merged into their side — for `formation.battleFor`. A side the viewer cannot
     read is `{}` and is drawn as its plain figures. */
  Object.entries(elements).forEach(([key, cellElements]) => {
    const at = cellElements.findIndex((el) => el.type === "combat")
    if (at < 0) return
    const armies = {}
    const add = (team, units) => {
      if (team !== "human" && team !== "zombie") return
      armies[team] = mergeUnits(armies[team] || {}, units || {})
    }
    Object.entries(game.metadata?.cells?.[key]?.metadata_by_user || {}).forEach(([id, entry]) => {
      if (Object.values(entry?.units || {}).some((unit) => (unit?.number ?? 0) > 0)) add(teamOf(id), entry.units)
    })
    ;(game.metadata?.movements || []).forEach((movement) => {
      const path = movement.path || []
      const cell = path[Math.max(0, Math.min(movement.current_index ?? 0, path.length - 1))]
      if (cell && `${cell[0]}-${cell[1]}` === key) add(teamOf(movement.user_id), movement.units)
    })
    // A battle is two sides; one the viewer cannot read is there all the same.
    armies.human = armies.human || {}
    armies.zombie = armies.zombie || {}
    // Whose each army is to the viewer, for its feet (S6): the viewer's side is theirs, the other
    // the enemy's; a viewer with no side (a spectator) gets plain shadows.
    const mine = teamOf(userId)
    const relations = mine ? { [mine]: "self", [mine === "human" ? "zombie" : "human"]: "enemy" } : {}
    elements[key] = cellElements.map((el, i) => (i === at ? { ...el, armies, relations } : el))
  })
  return { elements, movements }
}

/* Whether clicking a cell has anything to open. Shared with the replay for the same reason the
   board is: "a standing city, anyone's, or troops on unowned ground, or a march passing through"
   is a rule about the game, not about the view that happens to be drawing it — and a replay whose
   cells open on different terms from the game's is a replay of a different game.

   `hasCity` rides along on the click so the ui reducer can answer a second click on the
   already-selected cell by opening the city view directly, the same rule the CITY button uses. */
export const cellOpensAView = ({ game, payload, viewerId }) => {
  const cell = game?.metadata?.cells?.[`${payload.col}-${payload.row}`]
  // Anybody's troops standing here open the view (owner's R10), a teammate's camp included.
  const unitsHere = Object.values(cell?.metadata_by_user || {}).reduce(
    (total, metadata) =>
      total + Object.values(metadata?.units || {}).reduce((sum, unit) => sum + (unit?.number || 0), 0),
    0
  )
  const teamOf = (id) => game?.metadata?.users?.[String(id)]?.team
  // A march touching the cell counts as something to open (2026-08-25): the movement details are
  // the city view's business even on ground with no city.
  const marchesHere = movementsTouching(game?.metadata?.movements, payload, { userId: viewerId, teamOf }).length
  // A colony ordered here: the party may read zero free workers, and the map bar is where the
  // order is taken back.
  const founding = foundingOrderedAt({ metadata: game?.metadata, col: payload.col, row: payload.row })
  return cell?.owner != null || unitsHere > 0 || marchesHere > 0 || Boolean(founding)
}
